/**
 * @license
 * @copyright Copyright Motili Inc., 2021 All Rights Reserved
 */

import { ReactElement, useState } from 'react';
import { FileInput, Heading1, LoadingSpinner, Paragraph } from '@motili/ui';
import CenteredContainer from '../../../components/layout/CenteredContainer';
import { styled } from 'styled-components';
import { useAppSelector } from '../../../common/redux/hooks';
import BottomButtonNavigation from '../../../navigation/BottomButtonNavigation';
import FileCard from '../../../components/FileCard';

interface W9UploadProps {
    handleBackClick: () => void;
    handleNavigationAfterSubmit: () => void;
    handleFileUpload: (file: File) => Promise<unknown>;
}

export default function W9Upload({
    handleBackClick,
    handleNavigationAfterSubmit,
    handleFileUpload,
}: W9UploadProps): ReactElement {
    const steps = useAppSelector(
        ({ clientRegistration }) => clientRegistration.client.totalSteps
    );
    const [file, setFile] = useState<File | null>(null);
    const [busy, setBusy] = useState(false);

    const onDropAccepted = (files: File[]) => {
        setFile(files[0]);
    };

    const handleTrashClick = () => setFile(null);

    const handleSubmitClick = async () => {
        if (!file) {
            return;
        }
        try {
            setBusy(true);
            await handleFileUpload(file);
            // TODO Jro - submit data to documentDb
            setBusy(false);
            return handleNavigationAfterSubmit();
        } catch (error) {
            console.error(error);
            setBusy(false);
        }
    };

    return (
        <CenteredContainer>
            {busy && (
                <LoaderOverlay>
                    <LoadingSpinner fullscreen busy={busy} />
                </LoaderOverlay>
            )}
            <Paragraph>
                <BoldText>Step 4</BoldText> of {steps}
            </Paragraph>
            <Heading1>Upload your W-9 form</Heading1>
            <Paragraph>
                This space can be used for any explanation needed to accompany
                this specific document. If there is no need for this text it
                would not show and the inputs below would move up.
            </Paragraph>
            <Paragraph>
                Accepted file types are .png, .pdf, .jpg, and .doc.
            </Paragraph>
            <FileInput
                accept={{
                    'image/jpeg': [],
                    'image/png': [],
                    'application/pdf': [],
                    '.doc': [],
                    '.docx': [],
                }}
                onDrop={() => {}}
                onDropAccepted={onDropAccepted}
                onDropRejected={() => {}}
            />
            {file ? (
                <FileCard
                    fileName={file.name}
                    handleTrashClick={handleTrashClick}
                />
            ) : null}
            <BottomButtonNavigation
                onBackClick={handleBackClick}
                onNextClick={handleSubmitClick}
                nextButtonText={'Submit'}
                nextDisabled={!file || busy}
            />
        </CenteredContainer>
    );
}

const BoldText = styled.span`
    font-weight: bold;
`;

const LoaderOverlay = styled.div`
    top: 0;
    left: 0;
    position: fixed;
    width: 100%;
    height: 100%;
    background-color: gray;
    opacity: 0.3;
    align-items: center;
    justify-content: center;
    display: flex;
`;
